import React, { memo } from 'react';
import classNames from 'classnames';
import { Skeleton } from '@mui/material';

import { SL_WEB_CAM_PAYWALL_REDESIGN } from 'common/treatments';
import { useTreatments } from 'utils/treatments';

import MultiCamControls from 'components/MultiCamControls';
import RewindSelector from 'components/RewindSelector';
import { useUserPermissionStatus } from 'selectors/user';
import { ClipButtonTrackingProperties } from 'types/clips';
import { RewindPlaylists, SmartRewindPlaylistBlock } from 'utils/rewinds/playlistBlocks.types';
import LiveCamButton from 'components/LiveCamButton';
import { SpotDetails } from 'types/spot';
import ClipButtonWrapper from './ClipButtonWrapper/ClipButtonWrapper';
import styles from './CamRewindsControlBar.module.scss';

interface Props {
  isWavePool?: boolean;
  clipButtonSegmentProperties: ClipButtonTrackingProperties;
  currentPlaylist?: SmartRewindPlaylistBlock;
  disablePaywalls?: boolean;
  isFullscreen: boolean;
  isLive: boolean;
  isMultiCam: boolean;
  multiCamViewActive: boolean;
  rewindsLoading: boolean;
  rewindsPlaylists?: RewindPlaylists;
  setActiveMenu: React.Dispatch<React.SetStateAction<'NEARBY' | 'ANGLES'>>;
  setMultiCamViewActive: (multicamActive: boolean) => void;
  clipButtonUrl?: string;
  clipButtonDisabled?: boolean;
  showLiveButton?: boolean;
  spot?: SpotDetails;
  supportsSmartRewinds?: boolean;
  updateCurrentPlaylist: (currentPlaylistTime: string) => void;
  adRunning?: boolean;
}

const CamRewindsControlBar = ({
  isWavePool,
  clipButtonSegmentProperties,
  currentPlaylist,
  disablePaywalls,
  isFullscreen,
  isLive,
  isMultiCam,
  multiCamViewActive,
  rewindsLoading,
  rewindsPlaylists,
  setActiveMenu,
  setMultiCamViewActive,
  showLiveButton,
  spot,
  clipButtonUrl,
  clipButtonDisabled,
  supportsSmartRewinds,
  updateCurrentPlaylist,
  adRunning,
}: Props) => {
  const treatments = useTreatments();
  const { hasCamRewindPermissions } = useUserPermissionStatus();

  const isV2CamPaywallRedesignEnabled = treatments?.[SL_WEB_CAM_PAYWALL_REDESIGN] === 'on';
  const showClipButtonLoading =
    rewindsLoading &&
    !showLiveButton &&
    !(isV2CamPaywallRedesignEnabled && !hasCamRewindPermissions && !isWavePool);

  const shouldRenderClipButton =
    !rewindsLoading &&
    currentPlaylist &&
    !!rewindsPlaylists?.size &&
    !(isV2CamPaywallRedesignEnabled && !hasCamRewindPermissions && !isWavePool);

  return (
    <div className={styles.camRewindsControlBar}>
      <div
        className={classNames({
          [styles.controlsLeft]: true,
        })}
      >
        {!rewindsLoading &&
          currentPlaylist &&
          rewindsPlaylists &&
          !multiCamViewActive &&
          !adRunning && (
            <RewindSelector
              currentPlaylist={currentPlaylist}
              disablePaywalls={disablePaywalls}
              isLive={isLive}
              rewindsPlaylists={rewindsPlaylists}
              updateCurrentPlaylist={updateCurrentPlaylist}
            />
          )}
        {(rewindsLoading || adRunning) && (
          <Skeleton
            animation="pulse"
            className={styles.loading}
            variant="rounded"
            data-testid="skeleton-loading"
          />
        )}
      </div>
      {isMultiCam && !isFullscreen && (
        <MultiCamControls
          setMultiCamViewActive={setMultiCamViewActive}
          multiCamViewActive={multiCamViewActive}
          setActiveMenu={setActiveMenu}
        />
      )}
      <div
        className={classNames({
          [styles.controlsRight]: true,
        })}
      >
        {!multiCamViewActive && (
          <>
            {showClipButtonLoading && (
              <Skeleton
                animation="pulse"
                className={styles.clipButtonSkeleton}
                data-testid="clip-button-loading"
                variant="rounded"
              />
            )}
            {showLiveButton && isV2CamPaywallRedesignEnabled && !isWavePool && (
              <LiveCamButton
                className={styles.liveCamButton}
                dataTestId="controls-go-live-button"
                spot={spot}
              />
            )}
            {shouldRenderClipButton && (
              <ClipButtonWrapper
                clipButtonSegmentProperties={clipButtonSegmentProperties}
                data-testid="clip-button"
                disabled={clipButtonDisabled}
                disablePaywalls={disablePaywalls}
                href={clipButtonUrl}
                spotId={spot?._id}
                spotName={spot?.name}
                supportsSmartRewinds={supportsSmartRewinds}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default memo(CamRewindsControlBar);
